import type { TenantSettings } from '@pedigreesmart/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type SyntheticEvent } from 'react'

import { useSession } from '../session/SessionProvider.js'
import '../styles/forms.css'
import './settings.css'

/**
 * I dati dell'associazione.
 *
 * Non sono un dettaglio amministrativo: ragione sociale, partita IVA,
 * indirizzo e autorizzazioni **compaiono sui certificati** che l'associazione
 * emette. Finché questa schermata non è esistita non c'era modo di
 * correggerli — il permesso che li governa esisteva, e non aveva niente da
 * governare.
 *
 * **Lo slug e lo stato si mostrano e non si toccano.** Il primo è
 * nell'indirizzo pubblico della vetrina: cambiarlo romperebbe collegamenti già
 * pubblicati da altri. Il secondo lo decide chi fattura. Mostrarli comunque è
 * più onesto che nasconderli: sono veri, e chi guarda le impostazioni vuole
 * sapere dove si trova e in che stato è.
 */
export function TenantSettingsForm() {
  const { resources } = useSession()
  const cache = useQueryClient()
  const [errore, setErrore] = useState<string | null>(null)
  const [salvato, setSalvato] = useState(false)

  const dati = useQuery({
    queryKey: ['tenant-settings'],
    queryFn: ({ signal }) => resources.tenantSettings.read(signal),
  })

  const salva = useMutation({
    mutationFn: (corpo: Partial<Omit<TenantSettings, 'slug' | 'status'>>) =>
      resources.tenantSettings.save(corpo),
    onMutate: () => {
      setErrore(null)
      setSalvato(false)
    },
    onSuccess: () => {
      setSalvato(true)
      void cache.invalidateQueries({ queryKey: ['tenant-settings'] })
    },
    onError: (e: unknown) => {
      const problema = e as {
        status?: number
        violations?: readonly { message: string }[]
      }

      setErrore(
        problema.violations?.[0]?.message ??
          (problema.status === 403
            ? 'Solo il titolare può modificare i dati dell\'associazione.'
            : 'Non è stato possibile salvare.'),
      )
    },
  })

  if (dati.isPending) return <p className="messaggio">Caricamento…</p>

  if (dati.isError) {
    return (
      <p role="alert" className="messaggio messaggio--errore">
        Non è stato possibile leggere i dati dell'associazione.
      </p>
    )
  }

  const d = dati.data

  function invia(evento: SyntheticEvent<HTMLFormElement>) {
    evento.preventDefault()

    const campi = new FormData(evento.currentTarget)
    const corpo: Record<string, string | null> = {}

    for (const [chiave, valore] of campi.entries()) {
      if (typeof valore !== 'string') continue

      const pulito = valore.trim()

      corpo[chiave] = pulito === '' ? null : pulito
    }

    // `country` non è annullabile sul server: svuotarlo lascerebbe passare un
    // `null` che il controller ignora, ma mandarlo comunque sarebbe una
    // richiesta che dichiara un'intenzione che non ha.
    if (corpo['country'] === null) delete corpo['country']

    salva.mutate(corpo)
  }

  return (
    <section aria-labelledby="titolo-associazione">
      <div className="elenco__testa">
        <h1 id="titolo-associazione">{d.name}</h1>
        <span className="stato">{d.status}</span>
      </div>

      <p className="messaggio">
        Indirizzo pubblico della vetrina: <span className="codice">{d.slug}</span>. Non si cambia da
        qui: è già nei collegamenti che altri hanno pubblicato.
      </p>

      {errore && (
        <p role="alert" className="messaggio messaggio--errore">
          {errore}
        </p>
      )}

      {salvato && !errore && (
        <p className="messaggio" aria-live="polite">
          Dati salvati.
        </p>
      )}

      <form className="modulo" onSubmit={invia}>
        <fieldset>
          <legend>Denominazione</legend>
          <Campo nome="name" etichetta="Nome" valore={d.name} />
          <Campo nome="legal_name" etichetta="Ragione sociale" valore={d.legal_name} />
          <Campo
            nome="authorizations"
            etichetta="Riconoscimenti e autorizzazioni"
            valore={d.authorizations}
          />
        </fieldset>

        <fieldset>
          <legend>Dati fiscali</legend>
          <Campo nome="vat_number" etichetta="Partita IVA" valore={d.vat_number} />
          <Campo nome="tax_code" etichetta="Codice fiscale" valore={d.tax_code} />
        </fieldset>

        <fieldset>
          <legend>Sede</legend>
          <Campo nome="address" etichetta="Via" valore={d.address} />
          <Campo nome="postal_code" etichetta="CAP" valore={d.postal_code} />
          <Campo nome="city" etichetta="Città" valore={d.city} />
          <Campo nome="province" etichetta="Provincia" valore={d.province} />
          <Campo nome="country" etichetta="Paese" valore={d.country} />
        </fieldset>

        <fieldset>
          <legend>Contatti</legend>
          <Campo nome="phone" etichetta="Telefono" valore={d.phone} />
          <Campo nome="email" etichetta="Posta elettronica" valore={d.email} tipo="email" />
          <Campo nome="website" etichetta="Sito" valore={d.website} />
        </fieldset>

        <div className="modulo__azioni">
          <button type="submit" disabled={salva.isPending}>
            {salva.isPending ? 'Salvataggio…' : 'Salva'}
          </button>
        </div>
      </form>
    </section>
  )
}

function Campo({
  nome,
  etichetta,
  valore,
  tipo = 'text',
}: {
  nome: string
  etichetta: string
  valore: string | null
  tipo?: string
}) {
  return (
    <label className="modulo__campo">
      <span>{etichetta}</span>
      <input type={tipo} name={nome} defaultValue={valore ?? ''} />
    </label>
  )
}
